import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "网络剧", to: "/category/web" },
  { label: "专题", to: "/#specials" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    setMobileOpen(false);
    if (!query) return;
    navigate(`/category/all?q=${encodeURIComponent(query)}`);
    setKeyword("");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 md:h-20 items-center justify-between gap-4 px-4 md:px-6 w-full">
        <Link to="/" className="flex items-center gap-2 shrink-0" onClick={() => setMobileOpen(false)}>
          <span className="flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-movie-2-line text-lg md:text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg md:text-xl font-extrabold tracking-tight text-foreground-950">一路向西</span>
            <span className="text-[10px] md:text-xs text-foreground-500 tracking-[0.2em]">FILM &amp; STORY</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive && item.to !== "/#specials"
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 focus-within:border-primary-400 w-56 lg:w-64">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              type="search"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、导演、剧情"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              aria-label="站内搜索"
            />
          </form>

          <Link
            to="/category/all"
            className="hidden lg:inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-compass-3-line text-base" />
            全部影视
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800 lg:hidden"
            aria-label="打开菜单"
            aria-expanded={mobileOpen}
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 top-16 z-40 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileOpen(false)} />
          <div className="relative h-full w-4/5 max-w-xs overflow-y-auto bg-background-50 p-4">
            <form onSubmit={handleSearch} className="mb-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、导演、剧情"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                aria-label="站内搜索"
              />
            </form>
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between rounded-md px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}